import {
  Container,
  Grid,
  Header,
  Link,
  ProgressBar,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { PlaceBasic, Task, TASK_NAME_INFO_MAP, TASK_NAME_MAP } from '../common/types';
import { computeProgress, computeShowSourceRatings } from '../common/cardLogic';
import { StarsIcon } from './StarsIcon';
import { SourceRatingsSummary } from './SourceRatingsSummary';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Base report shape that works with both PlaceReport and PlaceReportPublic */
export type ScorecardLoadingReport = {
  place?: PlaceBasic | null;
  tasks: Task[];
  business_type?: string | null;
};

export type ScorecardLoadingViewProps = {
  /** The report being loaded */
  report: ScorecardLoadingReport;
  /** Current task being executed */
  currentTask?: Task;
  /** Overall status of the scorecard creation */
  status: 'in-progress' | 'success' | 'error';
  /** Whether to show the compact header (for inline use like home page) */
  compact?: boolean;
  /** Optional action button to show in the header */
  actionButton?: React.ReactNode;
};

/**
 * Unified component for displaying scorecard creation progress.
 * Shows:
 * - Progress bar with actual task progress (when available)
 * - Place details header
 * - Source ratings summary as data becomes available
 */
export function ScorecardLoadingView({
  report,
  currentTask,
  status,
  compact = false,
  actionButton,
}: ScorecardLoadingViewProps) {
  const [displayProgress, setDisplayProgress] = useState<number>(0);
  const displayProgressRef = useRef<number>(0);
  const maxDerivedProgressRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  const place = report.place;
  const currentName = currentTask ? TASK_NAME_MAP[currentTask.name] : 'Collecting details...';
  const currentInfo = currentTask
    ? TASK_NAME_INFO_MAP[currentTask.name]
    : 'Collecting details from Google Maps...';

  // Calculate progress: use actual task progress if available, otherwise estimate
  const progress = useMemo(() => computeProgress(currentTask), [currentTask]);

  useEffect(() => {
    displayProgressRef.current = displayProgress;
  }, [displayProgress]);

  useEffect(() => {
    const requestedProgress = status === 'in-progress' ? progress : 100;
    const clampedProgress = Math.max(0, Math.min(100, requestedProgress));
    const derivedProgress = Math.max(maxDerivedProgressRef.current, clampedProgress);
    maxDerivedProgressRef.current = derivedProgress;

    const from = displayProgressRef.current;
    const to = derivedProgress;

    if (to <= from + 0.0001) {
      setDisplayProgress(to);
      displayProgressRef.current = to;
      return;
    }

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const delta = to - from;
    const durationMs = Math.min(30000, Math.max(10000, delta * 500));

    let startTime: number | null = null;
    const animate = (timestamp: number): void => {
      if (startTime === null) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      const t = Math.min(1, elapsed / durationMs);
      // Quintic ease-out interpolation for stronger "fade-out" near the target.
      const easedT = 1 - (1 - t) ** 5;
      const nextValue = from + (to - from) * easedT;

      displayProgressRef.current = nextValue;
      setDisplayProgress(nextValue);

      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      displayProgressRef.current = to;
      setDisplayProgress(to);
      animationFrameRef.current = null;
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [progress, status]);

  // Determine if we should show the source ratings (when place data has been fetched)
  const showSourceRatings = useMemo(() => computeShowSourceRatings(place), [place]);

  // Check if this is an automotive business (for DealerRater tab)
  const isAutomotive = report.business_type === 'Automotive';
  const showDealerRater = isAutomotive || !!place?.dealerrater_link;

  // Check if this is an automotive business (for Cars.com tab)
  const showCarsCom = isAutomotive || !!place?.carscom_link;

  // Check if this is an automotive business (for CarFax tab)
  const showCarFax = isAutomotive || !!place?.carfax_link;

  // Check if this is an automotive business (for Edmunds tab)
  const showEdmunds = isAutomotive || !!place?.edmunds_link;

  // Check if this is an automotive business (for CarGurus tab)
  const showCarGurus = isAutomotive || !!place?.cargurus_link;

  // Check if this is a restaurant (for OpenTable tab)
  const isRestaurant = report.business_type === 'Restaurant';
  const showOpenTable = isRestaurant || !!place?.opentable_link;

  // Check if this is a Hotel, Restaurant, or Golf business (for TripAdvisor tab)
  const isHotel = report.business_type === 'Hotel';
  const isGolf = report.business_type === 'Golf';
  const showTripAdvisor = isHotel || isRestaurant || isGolf || !!place?.tripadvisor_link;

  // Check if this is a hotel (for Booking.com and Expedia tabs)
  const showBooking = isHotel || !!place?.booking_link;
  const showExpedia = isHotel || !!place?.expedia_link;

  // Check if this is a golf business (for GolfPass tab)
  const showGolfPass = isGolf || !!place?.golfpass_link;

  if (compact) {
    const compactContent = (
      <Container
        variant="stacked"
        header={
          <Header
            variant="h3"
            description={
              place ? (
                <SpaceBetween direction="vertical" size="xxxs">
                  {place.address}
                  <SpaceBetween direction="horizontal" size="xs">
                    {place.rating?.toFixed(1) ?? '0.0'}
                    <StarsIcon stars={place.rating ?? undefined} />
                    <Link href={place.cid_url ?? undefined} target="_blank">
                      {place.total_review_count?.toLocaleString() ?? '0'} Google reviews
                    </Link>
                  </SpaceBetween>
                </SpaceBetween>
              ) : undefined
            }
            actions={actionButton}
          >
            Creating Reputation Scorecard{place?.name ? ` for ${place?.name}` : '...'}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          <ProgressBar
            label={status === 'success' ? 'Completed creating scorecard' : currentName}
            status={status}
            value={displayProgress}
            additionalInfo={status === 'success' ? 'Scorecard created successfully' : currentInfo}
            resultText="Completed"
          />
          {showSourceRatings && place && (
            <SourceRatingsSummary
              place={place}
              showDealerRater={showDealerRater}
              showCarsCom={showCarsCom}
              showCarFax={showCarFax}
              showEdmunds={showEdmunds}
              showCarGurus={showCarGurus}
              showOpenTable={showOpenTable}
              showTripAdvisor={showTripAdvisor}
              showBooking={showBooking}
              showExpedia={showExpedia}
              showGolfPass={showGolfPass}
            />
          )}
        </SpaceBetween>
      </Container>
    );

    return compactContent;
  }

  // Full-page loading view
  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
          },
        },
      ]}
    >
      <Container
        header={
          <Header
            variant="h1"
            description={
              place
                ? `Working on your Reputation Scorecard for ${place.name}...`
                : `Working on your Reputation Scorecard...`
            }
          >
            Creating your Reputation Scorecard
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Container>
            {place ? (
              <Header
                variant="h2"
                description={
                  <span className="inline-flex flex-col gap-1">
                    <span>{place.address}</span>
                    <span className="inline-flex items-center gap-1">
                      <span>{place.rating?.toFixed(1) ?? '?.?'}</span>
                      <StarsIcon stars={place.rating ?? undefined} />
                      <Link href={place.cid_url ?? undefined} target="_blank">
                        {place.total_review_count?.toLocaleString() ?? '?'} Google reviews
                      </Link>
                    </span>
                  </span>
                }
              >
                {place.name}
              </Header>
            ) : (
              <Spinner size="large" />
            )}
          </Container>

          <ProgressBar
            label={currentName}
            status={status}
            value={displayProgress}
            additionalInfo={currentInfo}
          />

          {/* Show source ratings summary as data comes in */}
          {showSourceRatings && place && (
            <SourceRatingsSummary
              place={place}
              showDealerRater={showDealerRater}
              showCarsCom={showCarsCom}
              showCarFax={showCarFax}
              showEdmunds={showEdmunds}
              showCarGurus={showCarGurus}
              showOpenTable={showOpenTable}
              showTripAdvisor={showTripAdvisor}
              showBooking={showBooking}
              showExpedia={showExpedia}
              showGolfPass={showGolfPass}
            />
          )}
        </SpaceBetween>
      </Container>
    </Grid>
  );
}
